// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import apiFetch from '@wordpress/api-fetch';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { confirm } from '@automator/shared/utils/confirm';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware( UncannyAutomatorBackend.rest.nonce ) );

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-item-action-run-now-button' )
export class AutomatorLogActionCancelButton extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Number, attribute: 'item-id' } ) accessor itemID;

	@property( { type: Number, attribute: 'item-log-id' } ) accessor itemLogID;

	@property( { type: Boolean } ) accessor loading = false;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
		  ${ this._templateRunNowButton }
	   `;
	}

	/**
	 * Handles the cancel functionality
	 */
	async handleRunNowRequest() {
		// Check if it's loading, then abort
		if ( this.loading ) {
			return;
		}

		// Show confirmation dialog
		await confirm( {
			content: html`
				<uo-alert type="warning">
					${ escapeHTML( __( 'Are you sure you want to run this action now?', 'uncanny-automator' ) ) }
				</uo-alert>
			`
		} );

		// Do the request
		this.doRequest();
	}

	/**
	 * Do the request to run the action now
	 * It'll show the confirmation dialog before doing the request
	 * It'll also show the error message if the request fails
	 */
	doRequest() {
		// As soon as the call starts, set the loading animation
		this.loading = true;

		// Start call
		apiFetch( {
			url: `${ UncannyAutomatorBackend.rest.url }/async_run_now`,
			method: 'POST',
			data: {
				// eslint-disable-next-line camelcase
				item_log_id: this.itemLogID,
				item_id: this.itemID,
			}
		} )
			.then( ( response ) => {
				// Stop loading animation
				this.loading = false;

				// Request data refresh
				this.requestDataRefresh();
			} )
			.catch( async error => {
				// Stop loading animation
				this.loading = false;

				// Request data refresh
				this.requestDataRefresh();

				// Show the error in the console
				console.error( error );

				// Get the error message
				const fatalErrorMessage = ! isEmpty( error.message )
					? error.message
					: escapeHTML( __( 'Unknown error', 'uncanny-automator' ) );

				// Show the error, and offer the user the option to retry
				await confirm( {
					heading: escapeHTML( __( 'Something went wrong', 'uncanny-automator' ) ),
					content: html`
						<uo-alert type="error">
							${ fatalErrorMessage }
						</uo-alert>
					`,
					confirmButtonLabel: escapeHTML( __( 'Try again', 'uncanny-automator' ) ),
					cancelButtonLabel: escapeHTML( __( 'Close', 'uncanny-automator' ) )
				} );

				// Try again
				this.doRequest();
			} );
	}

	/**
	 * Send request to main component to refresh the data
	 */
	requestDataRefresh() {
		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-data-refresh', {
				bubbles: true,
				composed: true,
				detail: {
					// The ID used as the sidebar ID
					requestedBy: `item-${ this.itemID }`,
				}
			} )
		);
	}

	/**
	 * Creates the cancel button
	 * It'll create a disabled button if it's import mode
	 *
	 * @return {TemplateResult} The cancel button
	 */
	get _templateRunNowButton() {
		// Check if it's import mode
		if ( this.isImportMode ) {
			return html`
			 <uo-tooltip>
				${ escapeHTML( __( 'Running now is not possible in import mode', 'uncanny-automator' ) ) }

				<uo-button
				   slot="target"
				   size="small"
				   color="secondary"
				   disabled
				>
				   <uo-icon id="bolt"></uo-icon> ${ escapeHTML( __( 'Run now', 'uncanny-automator' ) ) }
				</uo-button>
			 </uo-tooltip>
		  `;
		}

		return html`
		  <uo-button
			 size="small"
			 color="secondary"

			 ?loading=${ this.loading }

			 @click=${ this.handleRunNowRequest }
		  >
			 <uo-icon id="bolt"></uo-icon> ${ escapeHTML( __( 'Run now', 'uncanny-automator' ) ) }
		  </uo-button>
	   `;
	}
}
